/**
 * `mod.tasks.*` — the bridge's task interface.
 *
 * This is what lets the Tasks APP be an ordinary installed app rather than a
 * fixture of the host. A task is an object entry on a session's log (RFC 0232),
 * which a sandboxed view has no way to reach: the log is Automerge state behind
 * a repo, and the bridge carries JSON over postMessage. So the view asks, and
 * the host — which already holds the repo — answers with `SessionTaskLog`.
 *
 * A CORE INTERFACE, not a capability, for the same reason `comments` is one:
 * there is no service to install and no document to mint. A capability (vcs) is
 * a thing with its own doc and lifecycle that a project either has or lacks;
 * tasks are already in every project, on the session logs, and an app is
 * simply granted a view of them. Registering it as a capability made the bridge
 * demand an install that could never happen from a host that had not registered
 * the capability — which is exactly the shape of gate a core interface avoids.
 *
 * Grants are still enforced (by the caller, uniformly with comments): reads
 * need the app's "tasks" read grant, writes its write grant. Looking at a task
 * list should not imply being able to close one.
 *
 * The implementation is deliberately the SAME `SessionTaskLog` the agent tools
 * and the CLI use — one definition of what a task is, three callers.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  SessionTaskLog,
  type Task,
  type TaskCompletion,
  type TaskEvent,
  type TaskPriority,
  type TaskRef,
  type TaskStatus,
} from '@mod/core/model'

/** The bridge namespace this module answers. */
export const TASKS_NAMESPACE = 'tasks'

/** What the bridge hands this handler. */
export interface TasksContext {
  repo: Repo
  projectId: string
  /** True when the app holds the "tasks" read grant (or the write grant). */
  canRead: boolean
  /** True when the app holds the "tasks" write grant. */
  canWrite: boolean
}

/** Copy Automerge-backed collections into values safe for postMessage. */
function transferableTask(task: Task): Task {
  return {
    id: task.id,
    title: task.title,
    ...(task.description ? { description: task.description } : {}),
    status: task.status,
    ...(task.priority ? { priority: task.priority } : {}),
    blockedBy: [...task.blockedBy],
    ...(task.parallelGroup ? { parallelGroup: task.parallelGroup } : {}),
    ...(task.parentId ? { parentId: task.parentId } : {}),
    subtaskIds: [...task.subtaskIds],
    refs: task.refs.map((r) => ({ ...r })),
    ...(task.events
      ? {
          events: task.events.map((e) => ({
            ...e,
            ...(e.refs ? { refs: e.refs.map((r) => ({ ...r })) } : {}),
          })),
        }
      : {}),
    ...(task.completion ? { completion: task.completion } : {}),
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    ...(task.completedAt ? { completedAt: task.completedAt } : {}),
    ...(task.completionNote ? { completionNote: task.completionNote } : {}),
  }
}

const COMPLETIONS: readonly TaskCompletion[] = ['manual', 'merge']

const STATUSES: readonly TaskStatus[] = [
  'pending',
  'active',
  'complete',
  'blocked',
  'cancelled',
]

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** Refuse a write from an app that only has a read grant. */
function requireWrite(ctx: TasksContext, method: string): void {
  if (!ctx.canWrite) {
    throw new Error(
      `this app is not granted "tasks" write access (needed for ${method})`,
    )
  }
}

/**
 * Handle one `tasks.*` bridge call. The caller has already matched the
 * namespace; this owns the verbs and the read/write split.
 */
export async function handleTasksCall(
  method: string,
  args: unknown[],
  ctx: TasksContext,
): Promise<unknown> {
  if (!ctx.canRead) {
    throw new Error('this app is not granted "tasks" access')
  }
  const log = new SessionTaskLog(ctx.repo as never, undefined, ctx.projectId)
  // `mod.tasks.list()` arrives as "tasks.list"; the namespace is already
  // matched by the caller, so only the verb matters here.
  const verb = method.slice(method.indexOf('.') + 1)
  switch (verb) {
    case 'list': {
      const opts = (args[0] ?? {}) as { includeCompleted?: boolean }
      const tasks = await log.listForProject(ctx.projectId, {
        includeCompleted: opts.includeCompleted === true,
      })
      return tasks.map(transferableTask)
    }

    /** The session lens (RFC 0232): what one session is working on. */
    case 'listForSession': {
      const sessionId = str(args[0])
      if (!sessionId) throw new Error('listForSession needs a session id')
      const opts = (args[1] ?? {}) as { includeCompleted?: boolean }
      const tasks = await log.listForSession(sessionId, {
        includeCompleted: opts.includeCompleted === true,
      })
      return tasks.map(transferableTask)
    }

    case 'get': {
      const id = str(args[0])
      if (!id) throw new Error('get needs a task id')
      const task = await log.get(id)
      return task ? transferableTask(task) : null
    }

    case 'create': {
      requireWrite(ctx, 'create')
      const input = (args[0] ?? {}) as {
        title?: string
        description?: string
        priority?: TaskPriority
        sessionId?: string
        refs?: TaskRef[]
        completion?: TaskCompletion
        parallelGroup?: string
        afterIds?: string[]
      }
      const title = str(input.title)
      if (!title) throw new Error('a task needs a title')
      const sessionId = str(input.sessionId)
      const task = await log.create(sessionId, {
        title,
        ...(input.description ? { description: input.description } : {}),
        ...(input.priority ? { priority: input.priority } : {}),
        ...(Array.isArray(input.refs) ? { refs: input.refs } : {}),
        ...(str(input.parallelGroup)
          ? { parallelGroup: str(input.parallelGroup) }
          : {}),
        ...(Array.isArray(input.afterIds)
          ? { afterIds: input.afterIds.map(str).filter(Boolean) }
          : {}),
        ...(COMPLETIONS.includes(input.completion as TaskCompletion)
          ? { completion: input.completion }
          : {}),
      })
      return transferableTask(task)
    }

    case 'update': {
      requireWrite(ctx, 'update')
      const id = str(args[0])
      if (!id) throw new Error('update needs a task id')
      const patch = (args[1] ?? {}) as {
        title?: string
        description?: string
        status?: TaskStatus
        priority?: TaskPriority
        completion?: TaskCompletion
        parallelGroup?: string
      }
      if (patch.status && !STATUSES.includes(patch.status)) {
        throw new Error(`"${patch.status}" is not a task status`)
      }
      // A merge-driven slice does not complete here (RFC 0319 3g); the view
      // reads the status it gets back rather than the one it asked for.
      const task = await log.update(id, {
        ...(patch.title ? { title: patch.title } : {}),
        ...(patch.description ? { description: patch.description } : {}),
        ...(patch.status ? { status: patch.status } : {}),
        ...(patch.priority ? { priority: patch.priority } : {}),
        ...(str(patch.parallelGroup)
          ? { parallelGroup: str(patch.parallelGroup) }
          : {}),
        ...(COMPLETIONS.includes(patch.completion as TaskCompletion)
          ? { completion: patch.completion }
          : {}),
      })
      return transferableTask(task)
    }

    /**
     * Something that happened, without moving the status (RFC 0319 3g). This
     * is what lets an app keep its own lifecycle evidence — a review verdict,
     * a deploy, a weekly move — on the shared task instead of in its own
     * store, which is the whole point of one task object.
     */
    case 'recordEvent': {
      requireWrite(ctx, 'recordEvent')
      const id = str(args[0])
      if (!id) throw new Error('recordEvent needs a task id')
      const input = (args[1] ?? {}) as Partial<TaskEvent>
      const type = str(input.type)
      if (!type) throw new Error('an event needs a type')
      const task = await log.recordEvent(id, {
        type,
        at: str(input.at) || new Date().toISOString(),
        ...(str(input.by) ? { by: str(input.by) } : {}),
        ...(str(input.note) ? { note: str(input.note) } : {}),
        ...(Array.isArray(input.refs) ? { refs: input.refs } : {}),
      })
      return transferableTask(task)
    }

    /** A checkbox line that arrives already ticked (RFC 0319 3g). */
    case 'importChecked': {
      requireWrite(ctx, 'importChecked')
      const id = str(args[0])
      if (!id) throw new Error('importChecked needs a task id')
      const note = str(args[1])
      const result = await log.importChecked(
        ctx.projectId,
        id,
        note || undefined,
      )
      return {
        task: transferableTask(result.task),
        ...(result.deferred ? { deferred: result.deferred } : {}),
      }
    }

    case 'complete': {
      requireWrite(ctx, 'complete')
      const id = str(args[0])
      if (!id) throw new Error('complete needs a task id')
      const note = str(args[1])
      const refs = Array.isArray(args[2]) ? (args[2] as TaskRef[]) : []
      const result = await log.complete(
        ctx.projectId,
        id,
        note || undefined,
        refs,
      )
      // Report what this unblocked: it is the whole reason dependencies are
      // tracked, and the surface should be able to say so. `deferred` is set
      // when the task's rule refused the completion (RFC 0319 3g), so a review
      // page can say "waiting on the merge" instead of drawing a tick.
      return {
        task: transferableTask(result.task),
        unblocked: result.unblockedTasks.map(transferableTask),
        ...(result.deferred ? { deferred: result.deferred } : {}),
      }
    }

    default:
      throw new Error(`mod.tasks has no method "${verb}"`)
  }
}
